import React, { useState, useEffect } from "react";
import { ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Legend } from "recharts";
import { Select, MenuItem, Box, Typography } from "@mui/material";
import { getCategoryStats, getAllCategoriesWithGroups } from "../../services/statsService";
import { getCategories } from "../../services/categoryService";
import { useGroupContext } from "../Groups/useGroupContext";
import { formatCurrency } from "../utils/currencyFormatter";

const CategoryStatsChart = ({ months, selectedCategory, setSelectedCategory, customDateRange, dateMode, userCurrency }) => {
  const [chartData, setChartData] = useState([]);
  const [average, setAverage] = useState(0);
  const [categories, setCategories] = useState([]);
  const { getContextParams, isPersonalMode } = useGroupContext();

  useEffect(() => {
    fetchCategories();
  }, [getContextParams]);

  useEffect(() => {
    if (selectedCategory) {
      fetchCategoryData(selectedCategory);
    }
  }, [selectedCategory, customDateRange]); 

  useEffect(() => {
    if (categories.length > 0 && selectedCategory) {
      const categoryExists = categories.some(cat => cat.id === selectedCategory);
      if (!categoryExists) {
        console.log('Selected category not found in current categories, clearing selection');
        setSelectedCategory("");
        localStorage.removeItem("selectedCategory");
      }
    }
  }, [categories, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const contextParams = getContextParams();
      let categoriesData;
      
      if (isPersonalMode) {
        categoriesData = await getAllCategoriesWithGroups(contextParams);
      } else {
        categoriesData = await getCategories(contextParams);
        categoriesData = categoriesData.map(cat => ({
          ...cat,
          display_name: cat.name,
          context_name: 'Group'
        }));
      }
      
      setCategories(categoriesData);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const fetchCategoryData = async (categoryId) => {
    try {
      const contextParams = getContextParams();
      let response;
      if(dateMode === "preset"){
        response = await getCategoryStats(months, categoryId, contextParams);
      } else{
        response = await getCategoryStats(customDateRange, categoryId, contextParams);
      }
      setChartData(response.category);
      calculateAverage(response.category);
    } catch (error) {
      console.error("Error fetching category data:", error);
    }
  };

  const calculateAverage = (data) => {
    if (data.length > 0) {
      const total = data.reduce((sum, item) => sum + item.amount, 0);
      setAverage((total / data.length).toFixed(2));
    } else {
      setAverage(0);
    }
  };

  const handleCategoryChange = (e) => {
    const newCategory = e.target.value;
    setSelectedCategory(newCategory);
    if (newCategory) {
      localStorage.setItem("selectedCategory", newCategory);
    } else {
      localStorage.removeItem("selectedCategory");
    }
  };

  // Helper function to determine if we should show yearly view
  const shouldShowYearlyView = () => {
    if (!customDateRange?.startDate || !customDateRange?.endDate) return false;
    
    const startDate = new Date(customDateRange.startDate);
    const endDate = new Date(customDateRange.endDate);
    const monthsDiff = (endDate.getFullYear() - startDate.getFullYear()) * 12 + 
                      (endDate.getMonth() - startDate.getMonth());
    
    return monthsDiff > 12;
  };

  const selectedCategoryData = categories.find((category) => category.id === selectedCategory);
  const categoryName = selectedCategoryData?.display_name || selectedCategoryData?.name || "Category";
  
  const isYearlyView = shouldShowYearlyView();
  const title = `${categoryName} Breakdown (${isYearlyView ? "Yearly View" : "Monthly View"})`;

  const monthOrder = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
  };

  let sortedData = [...chartData];
    
  if (isYearlyView) {
    sortedData.sort((a, b) => a.year - b.year);
  } else {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    sortedData.sort((a, b) => {
      if (a.year === b.year) {
        return monthOrder[a.month] - monthOrder[b.month]; 
      }
      return a.year - b.year; 
    });

    sortedData.sort((a, b) => {
      if (a.year === currentYear && monthOrder[a.month] === currentMonth) return 1;
      if (b.year === currentYear && monthOrder[b.month] === currentMonth) return -1;
      return 0;
    });
  }

  const values = sortedData.map((item) => Math.round(item.amount));

  const formatLabelForDisplay = (item, monthCount) => {
    if (isYearlyView) {
      return `Year ${item.year}`;
    }
    
    if (monthCount >= 12) {
      const shortYear = item.year.toString().substring(2);
      const shortMonth = item.month.substring(0, 3);
      return `${shortMonth} '${shortYear}`;
    }
    
    return `${item.month} ${item.year}`;
  };

  const rechartData = sortedData.map((item, index) => ({
    name: formatLabelForDisplay(item, months),
    amount: Math.round(item.amount),
    average: values.length > 1 ? parseFloat(average) : null
  }));

  const groupedCategories = categories.reduce((acc, category) => {
    const contextName = category.context_name || 'Personal';
    if (!acc[contextName]) {
      acc[contextName] = [];
    }
    acc[contextName].push(category);
    return acc;
  }, {});

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.98)', 
          padding: '12px 16px', 
          border: '1px solid #e0e0e0',
          borderRadius: '12px',
          fontSize: '14px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          fontWeight: '500',
          backdropFilter: 'blur(8px)'
        }}>
          {payload.map((pld, index) => (
            <p key={index} style={{ color: pld.color, margin: 0 }}>
              {pld.dataKey === 'average' 
                ? `Avg: ${formatCurrency(pld.value.toFixed(0), userCurrency)}` 
                : `${categoryName}: ${formatCurrency(pld.value.toFixed(0), userCurrency)}`
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const CustomizedLabel = ({ x, y, width, height, value }) => {
    if (!value) return null;
    return (
      <text 
        x={x + width / 2} 
        y={y + height / 2} 
        fill="black" 
        textAnchor="middle" 
        dominantBaseline="central"
        fontSize="14"
      >
        {value.toFixed(0)}
      </text>
    );
  };

  return (
    <Box>
      <Typography variant="h6">{title}</Typography>

      <Select
        value={selectedCategory}
        onChange={handleCategoryChange}
        sx={{ ml: 1, mb: 2, width: "300px", height: "40px" }}
        displayEmpty
      >
        <MenuItem value="">Select a category</MenuItem>
        {Object.entries(groupedCategories).map(([contextName, contextCategories]) => [
          <MenuItem key={`header-${contextName}`} disabled sx={{ 
            fontWeight: 'bold', 
            backgroundColor: 'rgba(0,0,0,0.1)',
            "&.Mui-disabled": {
              opacity: 1
            }
          }}>
            {contextName}
          </MenuItem>,
          ...contextCategories.map((category) => (
            <MenuItem key={category.id} value={category.id} sx={{ pl: 3 }}>
              {isPersonalMode ? category.display_name : category.name}
            </MenuItem>
          ))
        ]).flat()}
      </Select>

      {selectedCategory && sortedData.length > 0 ? (
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart data={rechartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
            <XAxis 
              dataKey="name" 
              tick={{ fontSize: months >= 12 ? 12 : 14, fill: 'black' }}
              interval={0}
              angle={months >= 12 ? -35 : 0}
              textAnchor={months >= 12 ? "end" : "middle"}
              height={months >= 12 ? 70 : 40}
            />
            <YAxis tick={{ fontSize: 14, fill: 'black' }} />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ fontSize: '14px', color: 'black' }}
              iconType="line"
              formatter={(value) => value === 'amount' ? 'amount' : value}
            />
            <Bar dataKey="amount" fill="#2196f3">
              <LabelList content={<CustomizedLabel />} />
            </Bar>
            {values.length > 1 && (
              <Line 
                type="monotone" 
                dataKey="average" 
                stroke="red" 
                strokeWidth={2}
                dot={{ fill: 'red', r: 3 }}
                name={`Average: ${formatCurrency(average, userCurrency)}`}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      ) : (
        <Typography>
          {selectedCategory ? "Loading chart data..." : "Select a category to view its breakdown"}
        </Typography>
      )}
    </Box>
  );
};

export default CategoryStatsChart;